export type AgentPhase = 'thinking' | 'planning' | 'working';

export interface ChatStreamPayload {
  agent_id?: string;
  code?: string;
  content?: string;
  details?: string;
  message?: string;
  reason?: string;
  retry_after?: number;
  session_id?: string;
  stage?: string;
  status_message?: string;
  tool_name?: string;
}

export interface ChatStreamEventData extends ChatStreamPayload {
  payload?: ChatStreamPayload;
}

const AGENT_PHASE_ORDER: AgentPhase[] = ['thinking', 'planning', 'working'];

const TOOL_STATUS_TRANSLATION_KEYS: Record<string, string> = {
  query_aifo_knowledge_base: 'query_aifo_knowledge_base',
  calculator: 'calculator',
  exa_search: 'exa_search',
  get_aifo_latest_price: 'get_aifo_latest_price',
  get_aifo_historical_prices: 'get_aifo_historical_prices',
  view_data: 'view_data',
  get_mt5_accounts: 'get_mt5_accounts',
  get_mt5_snapshot: 'get_mt5_snapshot',
  get_mt5_history: 'get_mt5_history',
  get_coach_report: 'get_coach_report',
  calculate_aifo_analytics: 'calculate_aifo_analytics',
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function normalizeChatStreamEventData(value: unknown): {
  eventData: ChatStreamEventData;
  payload: ChatStreamPayload;
} {
  if (!isRecord(value)) return { eventData: {}, payload: {} };

  const eventData = value as ChatStreamEventData;
  const payload = isRecord(eventData.payload) ? (eventData.payload as ChatStreamPayload) : eventData;

  return { eventData, payload };
}

export function advanceAgentPhase(currentPhase: AgentPhase, nextPhase: AgentPhase): AgentPhase {
  return AGENT_PHASE_ORDER.indexOf(nextPhase) >= AGENT_PHASE_ORDER.indexOf(currentPhase) ? nextPhase : currentPhase;
}

export function getToolStatusTranslationKey(toolName?: string): string | undefined {
  if (!toolName) return undefined;
  return TOOL_STATUS_TRANSLATION_KEYS[toolName];
}

export function resolveToolStatusMessage({
  language,
  statusMessage,
  toolName,
  translate,
}: {
  language: string;
  statusMessage?: string;
  toolName?: string;
  translate: (key: string) => string;
}): string | undefined {
  // English status text comes from the backend so it remains the source of truth.
  if (language === 'en') return statusMessage || undefined;

  const stableTranslationKey = getToolStatusTranslationKey(toolName);
  if (stableTranslationKey) {
    return translate(`toolStatus.${stableTranslationKey}`);
  }

  if (!statusMessage) return undefined;

  const translationKey = `toolStatus.${statusMessage}`;
  const translatedMessage = translate(translationKey);
  return translatedMessage === translationKey ? statusMessage : translatedMessage;
}

export function normalizeStreamErrorCode(code?: string): string | undefined {
  const normalizedCode = code?.trim().toLowerCase();
  return normalizedCode || undefined;
}

export function isStreamCompletionEvent(eventName: string): boolean {
  return eventName === 'done' || eventName === 'complete';
}
